/**
 * Workspace knowledge base API (Kong → kb-manager workspace-knowledge).
 */

import { getBearerAuthHeaders } from "@/lib/authSession";
import { fetchWithTimeout } from "@/lib/fetchWithTimeout";
import { kongBrowserFetchUrl } from "@/lib/kongApi";
import { getTenantApiWorkspaceId, tenantApiFetch } from "@/lib/tenantApi";

const PREFIX = "/api/v1/kb-manager-service/workspace-knowledge";

function _kbPath(suffix) {
  return `${PREFIX}${suffix.startsWith("/") ? suffix : `/${suffix}`}`;
}

function _authHeaders(extra = {}) {
  const headers = {
    Accept: "application/json",
    ...getBearerAuthHeaders(),
    ...extra,
  };
  const wid = getTenantApiWorkspaceId();
  if (wid) {
    headers["X-Workspace-Id"] = wid;
  }
  return headers;
}

async function _parseResponse(res) {
  const text = await res.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { raw: text };
    }
  }
  if (!res.ok) {
    const detail = data?.detail;
    const msg =
      (detail && typeof detail === "object" && detail.message) ||
      (typeof detail === "string" ? detail : null) ||
      data?.message ||
      res.statusText;
    const err = new Error(typeof msg === "string" ? msg : "Request failed");
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export class WorkspaceKnowledgeApiClient {
  listCollections() {
    return tenantApiFetch(_kbPath("/collections"));
  }

  getCollection(logicalId) {
    return tenantApiFetch(_kbPath(`/collections/${encodeURIComponent(logicalId)}`));
  }

  /** @param {{ display_name: string, description?: string }} body */
  createCollection(body) {
    return tenantApiFetch(_kbPath("/collections"), {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  deleteCollection(logicalId) {
    return tenantApiFetch(_kbPath(`/collections/${encodeURIComponent(logicalId)}`), {
      method: "DELETE",
    });
  }

  deleteSource(logicalId, sourceId) {
    return tenantApiFetch(
      _kbPath(
        `/collections/${encodeURIComponent(logicalId)}/sources/${encodeURIComponent(sourceId)}`
      ),
      { method: "DELETE" }
    );
  }

  /** @param {string} logicalId @param {File} file */
  async uploadDocument(logicalId, file) {
    const form = new FormData();
    form.append("file", file);
    const res = await fetchWithTimeout(
      kongBrowserFetchUrl(_kbPath(`/collections/${encodeURIComponent(logicalId)}/upload`)),
      {
        method: "POST",
        headers: _authHeaders(),
        body: form,
      }
    );
    return _parseResponse(res);
  }

  /** @param {string} logicalId @param {{ url: string, title?: string }} body */
  ingestUrl(logicalId, body) {
    return tenantApiFetch(_kbPath(`/collections/${encodeURIComponent(logicalId)}/url`), {
      method: "POST",
      body: JSON.stringify(body),
    });
  }
}

export const workspaceKnowledgeApi = new WorkspaceKnowledgeApiClient();
